import React, { useEffect, useState, useMemo } from 'react';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import {
	SCREEN,
	ContextualAnalyticsData,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useUIStateStore } from '@atlassian/jira-servicedesk-reports-common/src/controllers/ui-state-store/index.tsx';
import DateFilter from '@atlassian/jira-servicedesk-reports-common/src/ui/date-filter/main.tsx';
import Header from '@atlassian/jira-servicedesk-reports-common/src/ui/header/index.tsx';
import ErrorView from '@atlassian/jira-servicedesk-reports-common/src/ui/table/error/index.tsx';
import { Banner } from './banner/index.tsx';
import ExportCsvButton from './export-csv/index.tsx';
import ChartWithSummaryDetails from './main.tsx';
import type { AppProps } from './types.tsx';
import { useUrlActions, getCsvUrl, getCsvForGeneratedReport } from './utils.tsx';

const KbRequestsReports = ({
	routerProps,
	baseUrl,
	projectKey,
	projectName,
	summaryResource,
	drilldownResource,
}: AppProps) => {
	const { modifyPageUrl } = useUrlActions({ baseUrl, projectKey, routerProps, projectName });
	const [selectedDrillDownPoint, setDrillDownPoint] = useState<unknown>();
	const [{ dateFilterQuery }] = useUIStateStore();

	const { reportId } = routerProps;

	const {
		data: summaryData,
		error: summaryError,
		loading: summaryLoading,
		fetch: onFetchSummary,
	} = summaryResource;

	useEffect(() => {
		const fetchData = async () => {
			await onFetchSummary(dateFilterQuery);
		};
		fetchData();
		setDrillDownPoint(undefined);
	}, [dateFilterQuery, onFetchSummary]);

	const sourceName = useMemo(() => {
		switch (reportId) {
			case 'kb-requests-deflected':
				return 'viewRequestsDeflectedReport';
			case 'kb-requests-resolved':
				return 'viewRequestsResolvedReport';
			default:
				throw new Error(`${reportId} is an invalid reportId`);
		}
	}, [reportId]);

	if (summaryError !== undefined) {
		return <ErrorView />;
	}

	if (summaryData === undefined) {
		return <Spinner />;
	}
	let csvUrl;
	if (!summaryLoading) {
		csvUrl = getCsvForGeneratedReport(
			baseUrl,
			projectKey,
			dateFilterQuery,
			// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'SeriesData | undefined'.
			selectedDrillDownPoint,
			summaryData.taskId,
		);
	} else {
		// @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'SeriesData | undefined'.
		csvUrl = getCsvUrl(baseUrl, projectKey, dateFilterQuery, selectedDrillDownPoint);
	}

	return (
		<ContextualAnalyticsData sourceName={sourceName} sourceType={SCREEN}>
			<Header
				title={summaryData.label}
				projectKey={projectKey}
				projectName={projectName}
				baseUrl={baseUrl}
			>
				<ExportCsvButton href={csvUrl} />
			</Header>
			<Banner baseUrl={baseUrl} />
			<DateFilter
				loading={summaryLoading}
				routerProps={routerProps}
				lastSearchQuery={{
					startDate: summaryData.startDate,
					endDate: summaryData.endDate,
				}}
				onSelect={modifyPageUrl}
			/>
			<ChartWithSummaryDetails
				summaryResource={summaryResource}
				reportId={reportId}
				drilldownResource={drilldownResource}
				dateFilterQuery={dateFilterQuery}
				// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'SeriesData | undefined'.
				selectedDrillDownPoint={selectedDrillDownPoint}
				onSetDrillDownPoint={setDrillDownPoint}
			/>
			<FireScreenAnalytics key={reportId} />
		</ContextualAnalyticsData>
	);
};

export default KbRequestsReports;
